import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { Button } from "@chartvn/ui/button";

import { CareUnitFilter } from "../../features/care-units/components/care-unit-filter";
import { facilityPickQueryOptions } from "../../features/facility-pick/api/facilities";
import { FacilityChooser } from "../../features/facility-pick/facility-chooser";
import { WorklistFilterBar } from "../../features/worklist/components/worklist-filter-bar";
import { useResidentFilter } from "../../features/worklist/hooks/use-resident-filter";
import { Worklist } from "../../features/worklist/worklist";
import {
  anyFilterSet,
  mergeFilterChange,
  worklistSearchSchema,
  type WorklistFilters,
} from "../../features/worklist/worklist-filters";

/**
 * `RPM-004`'s worklist, for confirming communication and contact (`CR-44`, `J-535`).
 *
 * THE ROUTE COMPOSES THREE FEATURES, which is what `.claude/rules/coding.md` asks ("a feature never
 * imports another feature. Compose features in `routes/`"): the Building control is `facility-pick`'s,
 * the Care unit control is `care-units`', and the other six filters and the list are `worklist`'s.
 * The filters they share live in the address, so a reload, a session lock, a bookmark and Back to
 * worklist all return to the same list — every one but Resident, part of a name, which never goes in
 * an address (`D-1161`) and is held in memory instead.
 *
 * THE PAGE HEADER is the shell's, named by `staticData` (`22-COMPOUNDS-SHELL` §2.1): one h1 per
 * document. Before `J-535` this screen rendered a second one, and the shell's focus-to-heading
 * landed on the shell's placeholder "ChartVN".
 *
 * OPENING A ROW opens that entry on Enter vitals at its communication section, the `#entry-
 * communication` anchor, which reaching it there records (`RPM-053` row 1). The row's id is written
 * into this address first, so Back to worklist returns focus to it.
 */
function WorklistScreen() {
  const { opened, ...filters } = Route.useSearch();
  const navigate = Route.useNavigate();
  const [resident, setResident] = useResidentFilter();
  const picks = useQuery(facilityPickQueryOptions());

  function change(next: WorklistFilters) {
    // REPLACE, NOT PUSH: Back to worklist steps back over Enter vitals to the list as she left it,
    // not through every filter she tried on the way.
    void navigate({
      search: (current) => mergeFilterChange(current, filters, next),
      replace: true,
    });
  }

  async function open(alertId: string) {
    await navigate({ search: (prev) => ({ ...prev, opened: alertId }), replace: true });
    await navigate({
      to: "/vitals-entry",
      search: { alertId, from: "worklist" },
      hash: "entry-communication",
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <FiltersPanel
        filters={filters}
        onChange={change}
        resident={resident}
        onResident={setResident}
      />
      <Worklist
        filters={filters}
        resident={resident}
        zoneOf={(id) => picks.data?.items.find((b) => b.facilityId === id)?.ianaTimeZone ?? null}
        openedAlertId={opened}
        onOpenEntry={(alertId) => {
          void open(alertId);
        }}
      />
    </div>
  );
}

interface FiltersPanelProps {
  filters: WorklistFilters;
  onChange: (next: WorklistFilters) => void;
  resident: string;
  onResident: (typed: string) => void;
}

/** The eight filters: Building and Care unit from their own features, the other six the worklist's. */
function FiltersPanel({
  filters,
  onChange: change,
  resident,
  onResident: setResident,
}: FiltersPanelProps) {
  return (
    <section
      aria-label="Filters"
      data-testid="worklist-filters"
      className="flex flex-col gap-4 rounded-md border p-4"
    >
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-72">
          <FacilityChooser
            // A remount clears the chooser's text when "Every building" empties the filter.
            key={filters.facilityId ?? "every-building"}
            value={filters.facilityId}
            onChange={(facilityId) => {
              // A care unit is within a building: a new building starts with every unit.
              change({ ...filters, facilityId, careUnitIds: undefined });
            }}
          />
        </div>
        {filters.facilityId === undefined ? null : (
          <Button
            type="button"
            variant="outline"
            data-testid="worklist-every-building"
            className="min-h-11"
            onClick={() => {
              change({ ...filters, facilityId: undefined, careUnitIds: undefined });
            }}
          >
            Every building
          </Button>
        )}
      </div>
      {filters.facilityId === undefined ? null : (
        <CareUnitFilter
          facilityId={filters.facilityId}
          value={filters.careUnitIds ?? []}
          onChange={(ids) => {
            change({ ...filters, careUnitIds: ids.length === 0 ? undefined : ids });
          }}
        />
      )}
      <WorklistFilterBar
        filters={filters}
        onChange={change}
        resident={resident}
        onResident={setResident}
      />
      {anyFilterSet(filters) || resident !== "" ? (
        <div>
          <Button
            type="button"
            variant="outline"
            data-testid="worklist-clear-filters"
            className="min-h-11"
            onClick={() => {
              setResident("");
              change({});
            }}
          >
            Clear every filter
          </Button>
        </div>
      ) : null}
    </section>
  );
}

export const Route = createFileRoute("/_authenticated/worklist")({
  // THE PAGE HEADER IS THE SHELL'S (`RPM-004`'s "state: page header"): it reads this name, titles the
  // document with it, and moves focus to its one h1 on arrival. A second h1 here would take that focus.
  staticData: { screenName: "Worklist" },
  validateSearch: worklistSearchSchema,
  component: WorklistScreen,
});
